import React, { useEffect, useState, useMemo } from "react";
import cs from "classnames";
import { normalizeSortString } from "../../../lib/helpers/normalizeString";
import { Icon } from "../Icon/Icon";

import "./StructureTable.scss";

export type StructureTableColumn = {
  header: string | JSX.Element;
  key: string;
  classes?: string;
  hidden?: boolean;
  sortable?: boolean;
  sortKey?: string;
  onClick?: () => void;
};

export type StructureTableRows = {
  [key: string]: React.ReactNode;
};

interface StructureTableProps {
  small?: boolean;
  loading?: boolean;
  className?: string;
  rows: StructureTableRows[];
  columns: StructureTableColumn[];
  onRowClick?: (index: number) => void;
  noResultsPlaceholder?: string | React.ReactNode;
  isMobile?: boolean;
  disableHover?: boolean;
  // Sorting props - when provided, external sorting is used
  sortedColumnKey?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (key: string) => void;
  // Data for internal sorting - when provided, StructureTable handles sorting internally
  data?: any[];
  defaultSortColumn?: string;
  defaultSortDirection?: "asc" | "desc";
}

export const StructureTable = (props: StructureTableProps) => {
  const [rows, setRows] = useState<StructureTableRows[]>(() => props.rows);
  const [columns, setColumns] = useState<StructureTableColumn[]>(() =>
    props.columns.filter((item) => !item.hidden)
  );
  const [clickedRow, setClickedRow] = useState<number | null>(null);
  const [hoveredHeaderKey, setHoveredHeaderKey] = useState<string | null>(null);

  // Internal sorting state (only used when data prop is provided)
  const [internalSortedColumnKey, setInternalSortedColumnKey] =
    useState<string>(() => props.defaultSortColumn || "");
  const [internalSortDirection, setInternalSortDirection] = useState<
    "asc" | "desc"
  >(() => props.defaultSortDirection || "desc");

  // Determine if we're using internal or external sorting
  const isInternalSorting = props.data !== undefined;
  const currentSortedColumnKey = isInternalSorting
    ? internalSortedColumnKey
    : props.sortedColumnKey;
  const currentSortDirection = isInternalSorting
    ? internalSortDirection
    : props.sortDirection;

  // Helper function to get nested property value using dot notation
  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Internal sorting logic
  const sortedData = useMemo(() => {
    if (!isInternalSorting || !props.data || !currentSortedColumnKey) {
      return props.data || [];
    }

    const cloned = [...props.data];
    const sortColumn = props.columns.find(
      (col) => col.key === currentSortedColumnKey
    );
    const sortPath = sortColumn?.sortKey || currentSortedColumnKey;

    const compare = (a: any, b: any) => {
      let aVal = getNestedValue(a, sortPath);
      let bVal = getNestedValue(b, sortPath);

      // Handle string comparison (case insensitive, trimmed)
      if (typeof aVal === "string" && typeof bVal === "string") {
        const normalizedA = normalizeSortString(aVal);
        const normalizedB = normalizeSortString(bVal);
        return normalizedA.localeCompare(normalizedB);
      }

      // Handle numeric comparison
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum < bNum) return -1;
        if (aNum > bNum) return 1;
        return 0;
      }

      // Fallback to string comparison
      return normalizeSortString(String(aVal)).localeCompare(
        normalizeSortString(String(bVal))
      );
    };

    cloned.sort((x, y) =>
      currentSortDirection === "asc" ? compare(x, y) : compare(y, x)
    );
    return cloned;
  }, [
    props.data,
    currentSortedColumnKey,
    currentSortDirection,
    props.columns,
    isInternalSorting,
  ]);

  useEffect(() => {
    if (isInternalSorting && props.data) {
      // Convert sorted data to rows format for display
      const newRows = sortedData.map((item: any, index: number) => {
        const row: StructureTableRows = {};
        props.columns.forEach((col) => {
          if (!col.hidden) {
            // Check if there's a custom render function in the original rows
            const originalRowIndex = props.data!.indexOf(item);
            if (
              props.rows[originalRowIndex] &&
              props.rows[originalRowIndex][col.key]
            ) {
              row[col.key] = props.rows[originalRowIndex][col.key];
            } else {
              // Fallback to raw data value
              const value = getNestedValue(item, col.sortKey || col.key);
              row[col.key] = <span>{String(value || "")}</span>;
            }
          }
        });
        return row;
      });
      setRows(newRows);
    } else {
      setRows(props.rows);
    }
    setColumns(props.columns.filter((item) => !item.hidden));
  }, [props.rows, props.columns, sortedData, isInternalSorting, props.data]);

  if (rows && rows.length === 0) {
    return typeof props.noResultsPlaceholder === "string" ? (
      <h4>{props.noResultsPlaceholder}</h4>
    ) : (
      <>{props.noResultsPlaceholder}</>
    );
  }

  const handleRowClick = (index: number) => {
    if (props.onRowClick) {
      props.onRowClick(index);
    }

    // setClickedRow((prevClickedRow) =>
    //   prevClickedRow === index ? null : index
    // );
    setClickedRow(index);
  };

  const classes = cs(
    "StructureTable",
    {
      "StructureTable--small": props.small,
    },
    props.className
  );

  return (
    <div className={classes}>
      <div className="StructureTable__head mb-1 mt-2">
        {columns.map((col: StructureTableColumn) => (
          <h5
            key={col.key}
            className={`StructureTable__head__item ${col.key} ${
              col.classes ? col.classes : ""
            } ${col.sortable ? "sortable" : ""}`}
            onClick={(e) => {
              e.preventDefault();

              if (col.onClick) {
                col.onClick();
              }

              if (col.sortable) {
                if (isInternalSorting) {
                  // Handle internal sorting
                  if (col.key === currentSortedColumnKey) {
                    setInternalSortDirection((prev) =>
                      prev === "desc" ? "asc" : "desc"
                    );
                  } else {
                    setInternalSortedColumnKey(col.key);
                    setInternalSortDirection("desc");
                  }
                } else if (props.onSort) {
                  // Handle external sorting
                  props.onSort(col.key);
                }
              }
            }}
            onMouseEnter={() => setHoveredHeaderKey(col.key)}
            onMouseLeave={() =>
              setHoveredHeaderKey((prev) => (prev === col.key ? null : prev))
            }
          >
            <span>{col.header}</span>
            {col.sortable && (
              <span
                className={`StructureTable__head__item__sort-icon ${
                  currentSortedColumnKey === col.key
                    ? "sorted"
                    : hoveredHeaderKey === col.key
                    ? "inactive"
                    : "hidden"
                }`}
                aria-hidden
              >
                {currentSortedColumnKey === col.key ? (
                  currentSortDirection === "desc" ? (
                    <Icon icon="sorted-descending" />
                  ) : (
                    <Icon icon="sorted-ascending" />
                  )
                ) : hoveredHeaderKey === col.key ? (
                  <Icon icon="sorted-inactive" />
                ) : null}
              </span>
            )}
          </h5>
        ))}
      </div>
      <div className="StructureTable__content">
        {rows.map((row, i) => (
          <div
            key={`row-${i}`}
            className={cs(
              "StructureTable__item list-row",
              props.isMobile && clickedRow === i ? "clicked-row" : "",
              row?.disableHover && "disable-hover"
            )}
            // onClick={() => (props.onRowClick ? props.onRowClick(i) : undefined)}
            onClick={() => handleRowClick(i)}
          >
            {columns.map((rowColumn) => {
              return (
                <div
                  key={rowColumn.key}
                  className={cs(
                    "StructureTable__item__column",
                    rowColumn["key"]
                  )}
                >
                  {row[rowColumn["key"]]}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
